import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2, Edit } from 'lucide-react'

interface RoutingTarget {
  targetNumber: number
  deviceId: string
  phoneNumber?: string
  utteranceToSpeak: string
  utteranceLanguage: string
}

interface RoutingTargetListProps {
  targets: Record<string, RoutingTarget>
  onAdd?: () => void
  onEdit?: (targetNumber: number, target: RoutingTarget) => void
  onDelete?: (targetNumber: number) => void
}

export function RoutingTargetList({ 
  targets = {}, 
  onAdd, 
  onEdit, 
  onDelete 
}: RoutingTargetListProps) {
  const targetEntries = Object.entries(targets)
  const isEmpty = targetEntries.length === 0

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Weiterleitungsziele</h2>
        {!isEmpty && onAdd && (
          <Button
            variant="outline"
            size="sm"
            className="border-dashed border-brand-primary text-brand-primary hover:bg-brand-primary/10"
            onClick={onAdd}
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Weiterleitung hinzufügen
          </Button>
        )}
      </div>

      {/* Empty State */}
      {isEmpty ? (
        <div className="py-16 text-center">
          <div className="flex flex-col items-center justify-center gap-6">
            <div className="bg-brand-primary bg-opacity-10 p-6 rounded-full border border-brand-primary border-opacity-20">
              <Plus className="text-brand-primary h-8 w-8" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-gray-900">Keine Weiterleitungsziele</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Legen Sie Weiterleitungsziele fest, um Anrufe zu anderen Geräten oder Telefonnummern zu routen.
              </p>
            </div>
            {onAdd && (
              <Button
                className="bg-brand-primary hover:bg-brand-primary/90 text-white"
                onClick={onAdd}
              >
                <Plus className="w-5 h-5 mr-2" />
                Erste Weiterleitung hinzufügen
              </Button>
            )}
          </div>
        </div>
      ) : (
        /* Target List */
        <div className="space-y-4">
          {targetEntries.map(([key, target]) => (
            <Card 
              key={key}
              className="border border-gray-200 hover:shadow-md hover:border-brand-primary transition-all duration-200"
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  {/* Target Info */}
                  <div className="flex flex-col gap-3 flex-1">
                    {/* Target Number & Device */}
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Ziel-Nummer:</span>
                        <Badge className="bg-brand-primary/10 text-brand-primary font-medium">
                          {target.targetNumber}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Weitergeleitet an:</span>
                        <Badge className="bg-brand-primary/10 text-brand-primary font-medium">
                          {target.deviceId === 'CALL_FORWARD' 
                            ? target.phoneNumber || 'Telefonnummer'
                            : `Gerät ${target.deviceId}`
                          }
                        </Badge>
                      </div>
                    </div>

                    {/* Language & Utterance */}
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Sprache:</span>
                        <Badge className="bg-brand-primary/10 text-brand-primary font-medium">
                          {target.utteranceLanguage?.toUpperCase() || 'DE'}
                        </Badge>
                      </div>

                      <p className="text-gray-700 font-medium text-sm ml-1">
                        {target.utteranceToSpeak || 'Keine Ansage'}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {onEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white h-9 transition-colors"
                        onClick={() => onEdit(target.targetNumber, target)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Bearbeiten
                      </Button>
                    )}
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 hover:border-red-300 h-9 w-9 transition-colors"
                        onClick={() => onDelete(target.targetNumber)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
